import { useEffect, useState, useRef, memo } from "react";
import { motion, animate, useInView } from "framer-motion";
import { Users, Briefcase, Award, Headphones, Sparkles } from "lucide-react";

const statItems = [
  {
    end: 50,
    suffix: "+",
    label: "Clients",
    icon: Users,
    color: "#7C4DFF",
  },
  {
    end: 120,
    suffix: "+",
    label: "Projects",
    icon: Briefcase,
    color: "#27B8FF",
  },
  {
    end: 98,
    suffix: "%",
    label: "Client Satisfaction",
    icon: Award,
    color: "#A78BFA",
  },
  {
    end: 24,
    suffix: "/7",
    label: "Support",
    icon: Headphones,
    color: "#EC4899",
  },
  {
    end: 3,
    suffix: "+",
    label: "Years Experience",
    icon: Sparkles,
    color: "#10B981",
  },
];

const Counter = ({ end, suffix }) => {
  const [value, setValue] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  useEffect(() => {
    if (!isInView) return;

    const controls = animate(0, end, {
      duration: 2.2,
      ease: "easeOut",
      onUpdate: (latest) => setValue(latest),
    });

    return () => controls.stop();
  }, [isInView, end]);

  return (
    <span ref={ref}>
      {Math.round(value)}
      {suffix}
    </span>
  );
};

const Stats = () => {
  return (
    <section className="section-padding relative overflow-hidden">
      {/* Background gradients */}
      <div
        className="absolute inset-x-0 top-0 h-[400px] pointer-events-none opacity-20"
        style={{
          background: "radial-gradient(circle at 50% 50%, rgba(124, 77, 255, 0.15), transparent 70%)",
        }}
      />
      <div className="absolute inset-0 tech-grid opacity-10 pointer-events-none" />

      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="ai-badge mb-4 inline-flex">
            <Award className="h-3.5 w-3.5 text-ai-glow" />
            Track Record
          </span>
          <h2
            className="text-3xl font-bold text-white sm:text-4xl lg:text-5xl"
            style={{ fontFamily: '"Space Grotesk", sans-serif' }}
          >
            Our Impact in <span className="gradient-text">Numbers</span>
          </h2>
          <p className="mt-4 text-base text-slate-400 max-w-2xl mx-auto">
            We are committed to delivering enterprise-grade solutions with verified results.
          </p>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
          {statItems.map((stat, i) => {
            const IconComp = stat.icon;
            return (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                whileHover={{ y: -6, scale: 1.03 }}
                className="relative rounded-2xl p-6 text-center backdrop-blur-md transition-all duration-300"
                style={{
                  background: "rgba(15, 22, 48, 0.85)",
                  border: "1px solid rgba(255, 255, 255, 0.08)",
                  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
                }}
              >
                {/* Accent glow corner */}
                <div
                  className="absolute -right-8 -top-8 w-16 h-16 rounded-full opacity-0 group-hover:opacity-10 blur-xl transition-opacity duration-300 pointer-events-none"
                  style={{ background: stat.color }}
                />

                {/* Animated Icon */}
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-4"
                  style={{
                    background: `${stat.color}15`,
                    border: `1px solid ${stat.color}30`,
                  }}
                >
                  <IconComp className="w-6 h-6" style={{ color: stat.color }} />
                </div>

                {/* Counter */}
                <div
                  className="text-3xl sm:text-4xl font-extrabold text-white mb-2"
                  style={{
                    fontFamily: '"Space Grotesk", sans-serif',
                    background: `linear-gradient(135deg, ${stat.color}, #FFFFFF)`,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  <Counter end={stat.end} suffix={stat.suffix} />
                </div>

                {/* Label */}
                <div className="text-sm font-semibold tracking-wider text-[#A7B1D0] uppercase">
                  {stat.label}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      <div className="section-divider mt-20" />
    </section>
  );
};

export default memo(Stats);
